import {
  Text,
  Input,
  FormControl,
  FormErrorMessage,
  Button,
  Box,
  FormLabel,
} from "@chakra-ui/react";
import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import { parseErrorToUserMessage } from "../../../api/errors/parsers/parseErrorToUserMessage";

import { UserContext } from "../../../contexts/UserContext";
import { useNavigator, Route } from "../../../routers/navigator";
import { SignInFormContainer } from "../../atoms/Containers";
import { GoogleSignInForm } from "../GoogleSignInForm";

import { Form } from "./FormComponents";
import { SylaFormHeader } from "./SylaFormHeader";

interface SignInHookForm {
  userEmail: string;
  userPassword: string;
}

export const SignInForm = (): JSX.Element => {
  const { login } = useContext(UserContext);
  const navigate = useNavigator();

  const [signInError, setSignInError] = useState<unknown | undefined>();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SignInHookForm>();

  const onSubmit = async (data: SignInHookForm) => {
    if (!data.userEmail || !data.userPassword) {
      return;
    }

    setSignInError(undefined);
    const { userEmail, userPassword } = data;

    try {
      const { defaultAccountId } = await login(userEmail, userPassword);
      navigate({
        route: Route.Home,
        accountId: defaultAccountId,
        replace: true,
      });
    } catch (error) {
      setSignInError(error);
    }
  };

  function renderError(error: unknown): JSX.Element {
    const message = parseErrorToUserMessage(error).description;
    return (
      <FormControl isInvalid={true}>
        <FormErrorMessage>{message}</FormErrorMessage>
      </FormControl>
    );
  }

  const history = useHistory();

  return (
    <SignInFormContainer>
      <Box>
        <SylaFormHeader heading={"Sign in"} />
      </Box>
      {/* Google sign-in support */}
      <GoogleSignInForm />
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box>
          <FormControl
            isDisabled={isSubmitting}
            isInvalid={errors?.userEmail !== undefined}
            mb="20px"
          >
            <FormLabel>Email address</FormLabel>
            <Input
              type="email"
              placeholder="Type your email"
              {...register("userEmail", {
                required: "Email is missing",
                validate: (email?: string) =>
                  isEmail(email || "") ? undefined : "Invalid email address",
              })}
              backgroundColor="white"
              autoComplete="email"
            />
            {errors.userEmail && (
              <FormErrorMessage>{errors.userEmail.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl
            isDisabled={isSubmitting}
            isInvalid={errors?.userPassword !== undefined}
            mb="30px"
          >
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              placeholder="Type your password"
              {...register("userPassword", { required: "Password is missing" })}
              backgroundColor="white"
              autoComplete="current-password"
            />
            {errors.userPassword && (
              <FormErrorMessage>{errors.userPassword.message}</FormErrorMessage>
            )}
          </FormControl>

          <Button
            variant="solid"
            colorScheme="primary"
            onClick={() => {}}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            width="full"
            type="submit"
          >
            Sign in
          </Button>
          {signInError && renderError(signInError)}
          <Text mt="20px">
            <Button
              color="primary.500"
              disabled={isSubmitting}
              onClick={() =>
                navigate({
                  route: Route.ForgotPassword,
                  state: {
                    email: getValues("userEmail"),
                  },
                })
              }
              variant="link"
            >
              Forgot your password?
            </Button>
          </Text>
          <Text mt="20px">
            {"Are you new here? "}
            <Button
              color="primary.500"
              disabled={isSubmitting}
              onClick={() => history.push("/sign-up")}
              variant="link"
            >
              Sign up today
            </Button>
          </Text>
        </Box>
      </Form>
    </SignInFormContainer>
  );
};
